// Derive the sidebar "new matter" pip colour from the tenant's brand (nav)
// colour set on QA. The pip and the newness-slider handle used to share
// var(--me-nav-color) and were therefore identical; the pip now gets a
// deliberately DIFFERENT, non-urgent companion colour (Ken 2026-08-19).
//
// Method: the brand drives the HUE (rotated to a split-complement); saturation
// and lightness are pinned to a calm band so every brand — pale cream default,
// earthy tone, or neon bright — yields a muted, legible, non-alarming pip that
// still reads as intentionally related to the brand.

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  let h = 0, s = 0
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      default: h = (r - g) / d + 4
    }
    h *= 60
  }
  return [h, s, l]
}

function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0, g = 0, b = 0
  if (h < 60) { r = c; g = x }
  else if (h < 120) { r = x; g = c }
  else if (h < 180) { g = c; b = x }
  else if (h < 240) { g = x; b = c }
  else if (h < 300) { r = x; b = c }
  else { r = c; b = x }
  const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase()
}

// Derived from the default cream brand; also the CSS fallback for --me-new-pip.
export const DEFAULT_NEW_PIP = '#608DA9'

export function deriveNewPipColor(brandHex: string): string {
  const rgb = hexToRgb(brandHex)
  if (!rgb) return DEFAULT_NEW_PIP
  const [h, s] = rgbToHsl(rgb[0], rgb[1], rgb[2])
  let hue = (h + 165) % 360                 // split-complement — clearly different, still related
  if (hue >= 335 || hue < 25) hue = 205     // red/alarm band → calm blue (keeps the pip non-urgent)
  const sat = s > 0.08 ? Math.min(Math.max(s, 0.30), 0.50) : 0.38  // calm: never full, never gray
  const light = 0.54                        // mid-tone: legible on both light and dark sidebar surfaces
  return hslToHex(hue, sat, light)
}
